import type { CSSProperties } from 'react';

import { cx } from '@/shared/lib/cx';

import styles from './Skeleton.module.scss';

export interface SkeletonProps {
  /** Ширина: число — пиксели, строка — как в CSS (`'60%'`, `'12ch'`). */
  width?: number | string;
  height?: number | string;
  /** Круглый — для аватаров. */
  circle?: boolean;
  className?: string;
}

/**
 * Заглушка на время загрузки.
 *
 * Скелетон, а не спиннер: он повторяет форму того, что появится, и экран не
 * прыгает, когда данные приедут. Спиннер остаётся только внутри кнопок
 * (концепт, §8). Мерцание выключается вместе с prefers-reduced-motion.
 */
export function Skeleton({ width, height = 16, circle = false, className }: SkeletonProps) {
  const style: CSSProperties = { width, height };

  if (circle) {
    style.width = width ?? height;
  }

  return (
    <span
      className={cx(styles['skeleton'], circle && styles['skeleton--circle'], className)}
      style={style}
      aria-hidden="true"
    />
  );
}

/** Несколько строк текста разной длины — чтобы блок не выглядел таблицей. */
export function SkeletonText({ lines = 3 }: { lines?: number }) {
  const widths = ['100%', '92%', '76%', '88%', '64%'];

  return (
    <span className={styles['skeleton-text']} aria-hidden="true">
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} width={widths[i % widths.length] ?? '100%'} height={12} />
      ))}
    </span>
  );
}
